import { useMemo } from 'react';
import { Select } from '@/components/ui/Select';
import { sitesForOrganization } from '@/features/organizations/hierarchy';
import { SCOPE_LEVELS, SCOPE_LEVEL_HELP, type ScopeLevel, type ScopeSelection } from './scope-selection';
import type { DeviceGroup, DeviceListRow, Organization, Site } from '@/lib/api/types';

/*
  Chooses the scope node for a role assignment.

  The schema constraint is ck_role_assignments_single_scope: at most one of organization_id,
  site_id, device_group_id, device_id is set, and none set means tenant-wide. So the picker is a
  level selector plus, for the levels that need one, a node selector. It cannot express two nodes
  at once, which is the constraint made unreachable rather than validated.

  The narrowing runs downward and each level filters the next, in the same way the device move
  dialog works: organization, then that organization's sites, then groups valid at the chosen site,
  then devices inside the chosen group or site. Choosing a broader level discards the narrower
  selections, because a site id from a different organization is exactly the corrupt combination the
  constraint cannot catch.

  The state type, the level list and the completeness check live in ./scope-selection.ts.
*/

const UNSET = '__unset__';

export interface ScopePickerProps {
  readonly selection: ScopeSelection;
  readonly onChange: (next: ScopeSelection) => void;
  readonly organizations: readonly Organization[];
  readonly sites: readonly Site[];
  readonly groups: readonly DeviceGroup[];
  readonly devices: readonly DeviceListRow[];
}

export function ScopePicker({
  selection,
  onChange,
  organizations,
  sites,
  groups,
  devices,
}: ScopePickerProps): React.JSX.Element {
  const siteOptions = useMemo(
    () => sitesForOrganization(sites, selection.organizationId),
    [sites, selection.organizationId],
  );

  // For a group scope the site is optional, because an organization wide group has no site. So
  // the group list is every group in the organization when no site is chosen, and narrows to
  // the site's groups plus the organization wide ones when one is.
  const groupOptions = useMemo(() => {
    if (selection.organizationId === null) {
      return [];
    }
    return groups
      .filter((group) => {
        if (group.organizationId !== selection.organizationId) {
          return false;
        }
        if (selection.siteId === null) {
          return true;
        }
        return group.siteId === null || group.siteId === selection.siteId;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [groups, selection.organizationId, selection.siteId]);

  const deviceOptions = useMemo(() => {
    if (selection.organizationId === null) {
      return [];
    }
    return devices
      .filter((row) => {
        if (row.device.organizationId !== selection.organizationId) {
          return false;
        }
        if (selection.siteId !== null && row.device.siteId !== selection.siteId) {
          return false;
        }
        if (
          selection.deviceGroupId !== null &&
          row.device.deviceGroupId !== selection.deviceGroupId
        ) {
          return false;
        }
        return true;
      })
      .sort((a, b) => a.device.hostname.localeCompare(b.device.hostname));
  }, [devices, selection.organizationId, selection.siteId, selection.deviceGroupId]);

  const needsOrganization = selection.level !== 'tenant';
  const needsSite = selection.level === 'site';
  const offersSite = selection.level === 'device_group' || selection.level === 'device';
  const needsGroup = selection.level === 'device_group';
  const offersGroup = selection.level === 'device';
  const needsDevice = selection.level === 'device';

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Select
          label="Scope"
          value={selection.level}
          options={SCOPE_LEVELS.map((level) => ({ value: level.value, label: level.label }))}
          onValueChange={(next) => {
            const level = next as ScopeLevel;
            // Narrower selections are dropped when the level changes. Keeping them would let a
            // site id from a previous organization survive into a new scope.
            onChange({
              level,
              organizationId: level === 'tenant' ? null : selection.organizationId,
              siteId: null,
              deviceGroupId: null,
              deviceId: null,
            });
          }}
        />
        <p className="mt-1.5 text-xs text-fg-subtle">{SCOPE_LEVEL_HELP[selection.level]}</p>
      </div>

      {needsOrganization ? (
        <Select
          label="Organization"
          value={selection.organizationId ?? UNSET}
          options={[
            { value: UNSET, label: 'Choose an organization' },
            ...organizations.map((org) => ({
              value: org.id,
              label: org.status === 'active' ? org.name : `${org.name} (${org.status})`,
            })),
          ]}
          onValueChange={(next) => {
            onChange({
              ...selection,
              organizationId: next === UNSET ? null : next,
              siteId: null,
              deviceGroupId: null,
              deviceId: null,
            });
          }}
        />
      ) : null}

      {needsSite || offersSite ? (
        <Select
          label={needsSite ? 'Site' : 'Site (optional, narrows the list below)'}
          value={selection.siteId ?? UNSET}
          options={[
            {
              value: UNSET,
              label: needsSite ? 'Choose a site' : 'Any site in this organization',
            },
            ...siteOptions.map((site) => ({ value: site.id, label: site.name })),
          ]}
          onValueChange={(next) => {
            onChange({
              ...selection,
              siteId: next === UNSET ? null : next,
              deviceGroupId: null,
              deviceId: null,
            });
          }}
        />
      ) : null}

      {needsGroup || offersGroup ? (
        <Select
          label={needsGroup ? 'Device group' : 'Device group (optional, narrows the list below)'}
          value={selection.deviceGroupId ?? UNSET}
          options={[
            {
              value: UNSET,
              label: needsGroup ? 'Choose a device group' : 'Any group',
            },
            ...groupOptions.map((group) => ({
              value: group.id,
              label: group.siteId === null ? `${group.name} (organization wide)` : group.name,
            })),
          ]}
          onValueChange={(next) => {
            onChange({
              ...selection,
              deviceGroupId: next === UNSET ? null : next,
              deviceId: null,
            });
          }}
        />
      ) : null}

      {needsDevice ? (
        <Select
          label="Device"
          value={selection.deviceId ?? UNSET}
          options={[
            { value: UNSET, label: 'Choose a device' },
            ...deviceOptions.map((row) => ({
              value: row.device.id,
              label: `${row.device.hostname}${row.siteName === null ? '' : ` (${row.siteName})`}`,
            })),
          ]}
          onValueChange={(next) => {
            onChange({ ...selection, deviceId: next === UNSET ? null : next });
          }}
        />
      ) : null}

      {needsDevice && deviceOptions.length === 0 && selection.organizationId !== null ? (
        <p className="text-xs text-[var(--color-warning)]">
          No devices match that organization, site and group combination, so there is nothing to
          scope to. Widen the site or group above.
        </p>
      ) : null}
    </div>
  );
}
